import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Phone, User, Edit3, Trash2, Plus, RefreshCw, AlertCircle, Check, X, MessageCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const WhatsAppManagement = () => {
  const [numbers, setNumbers] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPhone, setEditingPhone] = useState(null);
  const [formData, setFormData] = useState({
    phone: '',
    username: '',
    description: ''
  });
  const [editFormData, setEditFormData] = useState({
    phone: '',
    username: '',
    description: ''
  });

  const { toast } = useToast();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Charger les données
  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [numbersResponse, usersResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/whatsapp`),
        axios.get(`${BACKEND_URL}/api/users`)
      ]);
      
      setNumbers(numbersResponse.data.numbers || []);
      setUsers(usersResponse.data.users || []);
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      setError('Erreur lors du chargement des données');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Créer un numéro
  const handleCreateNumber = async () => {
    if (!formData.phone || !formData.username) {
      toast({
        title: "Erreur",
        description: "Le numéro et l'utilisateur sont requis",
        variant: "destructive",
      });
      return;
    }

    try {
      await axios.post(`${BACKEND_URL}/api/whatsapp`, formData);
      
      toast({
        title: "Succès",
        description: "Numéro WhatsApp ajouté avec succès",
      });

      setFormData({ phone: '', username: '', description: '' });
      setShowCreateForm(false);
      loadData();
    } catch (error) {
      console.error('Erreur création numéro:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de l'ajout",
        variant: "destructive",
      });
    }
  };

  // Supprimer un numéro
  const handleDeleteNumber = async (phone) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le numéro ${phone} ?`)) {
      return;
    }

    try {
      await axios.delete(`${BACKEND_URL}/api/whatsapp/${phone}`);
      
      toast({
        title: "Succès",
        description: "Numéro WhatsApp supprimé avec succès",
      });

      loadData();
    } catch (error) {
      console.error('Erreur suppression numéro:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de la suppression",
        variant: "destructive",
      });
    }
  };

  // Commencer l'édition
  const handleStartEdit = (number) => {
    setEditingPhone(number.phone);
    setEditFormData({
      phone: number.phone,
      username: number.username,
      description: number.description || ''
    });
  };

  // Annuler l'édition
  const handleCancelEdit = () => {
    setEditingPhone(null);
    setEditFormData({
      phone: '',
      username: '',
      description: ''
    });
  };

  // Sauvegarder les modifications
  const handleUpdateNumber = async (oldPhone) => {
    if (!editFormData.phone || !editFormData.username) {
      toast({
        title: "Erreur",
        description: "Le numéro et l'utilisateur sont requis",
        variant: "destructive",
      });
      return;
    }

    try {
      await axios.put(`${BACKEND_URL}/api/whatsapp/${oldPhone}`, editFormData);
      
      toast({
        title: "Succès",
        description: "Numéro WhatsApp mis à jour avec succès",
      });

      setEditingPhone(null);
      setEditFormData({ phone: '', username: '', description: '' });
      loadData();
    } catch (error) {
      console.error('Erreur modification numéro:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de la modification",
        variant: "destructive",
      });
    }
  };

  // Obtenir le nombre de numéros par utilisateur
  const getNumberCountByUser = (username) => {
    return numbers.filter(n => n.username === username).length;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2">
              <MessageCircle className="w-5 h-5 text-green-600" />
              <span>Gestion WhatsApp</span>
              <Badge variant="secondary" className="ml-2">
                {numbers.length} {numbers.length > 1 ? 'numéros' : 'numéro'}
              </Badge>
            </CardTitle>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={loadData}
                disabled={isLoading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Actualiser
              </Button>
              <Button
                size="sm"
                onClick={() => setShowCreateForm(!showCreateForm)}
                className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nouveau numéro
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Formulaire de création */}
      {showCreateForm && (
        <Card className="border-green-200 bg-green-50/30">
          <CardHeader>
            <CardTitle className="text-lg">Ajouter un numéro WhatsApp</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  Numéro WhatsApp
                </label>
                <Input
                  placeholder="+33612345678"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  Utilisateur
                </label>
                <Select value={formData.username} onValueChange={(value) => setFormData({ ...formData, username: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un utilisateur" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(user => (
                      <SelectItem key={user.username} value={user.username}>
                        {user.username} ({getNumberCountByUser(user.username)} numéro{getNumberCountByUser(user.username) > 1 ? 's' : ''})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  Description (optionnel)
                </label>
                <Input
                  placeholder="Ex: Personnel, Professionnel..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCreateForm(false);
                  setFormData({ phone: '', username: '', description: '' });
                }}
              >
                <X className="w-4 h-4 mr-2" />
                Annuler
              </Button>
              <Button
                onClick={handleCreateNumber}
                className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
              >
                <Check className="w-4 h-4 mr-2" />
                Ajouter
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Erreur */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center space-x-3">
          <AlertCircle className="w-5 h-5" />
          <span className="text-sm font-medium">{error}</span>
        </div>
      )}

      {/* Liste des numéros */}
      <div className="grid grid-cols-1 gap-4">
        {numbers.map((number) => {
          const isEditing = editingPhone === number.phone;
          const user = users.find(u => u.username === number.username);
          
          return (
            <Card key={number.phone} className={`border transition-shadow ${
              isEditing ? 'border-green-400 shadow-lg bg-green-50/20' : 'border-slate-200 hover:shadow-md'
            }`}>
              <CardContent className="p-6">
                {isEditing ? (
                  /* Mode édition */
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-slate-800">
                        Éditer le numéro
                      </h3>
                      <Badge className="bg-green-100 text-green-700">
                        En cours d'édition
                      </Badge>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-1 block">
                          Numéro WhatsApp
                        </label>
                        <Input
                          placeholder="+33612345678"
                          value={editFormData.phone}
                          onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-1 block">
                          Utilisateur
                        </label>
                        <Select value={editFormData.username} onValueChange={(value) => setEditFormData({ ...editFormData, username: value })}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {users.map(user => (
                              <SelectItem key={user.username} value={user.username}>
                                {user.username}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-1 block">
                          Description
                        </label>
                        <Input
                          placeholder="Description..."
                          value={editFormData.description}
                          onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="flex justify-end space-x-2 pt-2">
                      <Button
                        variant="outline"
                        onClick={handleCancelEdit}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Annuler
                      </Button>
                      <Button
                        onClick={() => handleUpdateNumber(number.phone)}
                        className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                      >
                        <Check className="w-4 h-4 mr-2" />
                        Sauvegarder
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* Mode affichage normal */
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <Phone className="w-6 h-6 text-white" />
                      </div>
                      
                      <div>
                        <div className="flex items-center space-x-3 mb-1">
                          <h3 className="text-lg font-semibold text-slate-800">{number.phone}</h3>
                          {number.description && (
                            <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200">
                              {number.description}
                            </Badge>
                          )}
                        </div>
                        
                        <div className="flex items-center space-x-2 text-sm text-slate-600">
                          <User className="w-4 h-4" />
                          <span className="font-medium">{number.username}</span>
                          {user && (
                            <span className="text-slate-400">• {user.email}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleStartEdit(number)}
                        className="text-green-600 hover:text-green-700 hover:bg-green-50"
                      >
                        <Edit3 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteNumber(number.phone)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* État vide */}
      {!isLoading && numbers.length === 0 && (
        <Card>
          <CardContent className="text-center py-12">
            <MessageCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Aucun numéro WhatsApp configuré</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default WhatsAppManagement;
